import React, { useEffect, useState } from 'react';
import { getImage } from 'hooks/get-image';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { userAssets } from 'store/asset';
import { useNavigate } from 'react-router-dom';
import { deleteRequest, userRequests } from 'store/request';
import Button from 'components/ui/Button';
import { logout } from 'store/auth';
import { motion, AnimatePresence } from 'framer-motion';

const OwnedAssets = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [deletingRequests, setDeletingRequests] = useState({});

  const { user: assets } = useSelector(state => state.asset);
  const { user: requests } = useSelector(state => state.request);
  const { language } = useSelector((state) => state.app);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      dispatch(userAssets()),
      dispatch(userRequests())
    ])
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  const handleCancel = (id) => {
    // Set this specific request as deleting
    setDeletingRequests(prev => ({ ...prev, [id]: true }));

    dispatch(deleteRequest(id))
      .then((res) => {
        if (res.payload.Result) {
          dispatch(userRequests())
            .then(() => {
              // Clear the deleting state after the requests are refreshed
              setDeletingRequests(prev => ({ ...prev, [id]: false }));
            });
        } else {
          // If there was an error, stop the loading animation
          setDeletingRequests(prev => ({ ...prev, [id]: false }));
        }
      })
      .catch(() => {
        // Also handle potential exceptions
        setDeletingRequests(prev => ({ ...prev, [id]: false }));
      });
  }

  const handleLogout = () => {
    dispatch(logout());
    navigate('/intro');
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6 animate-pulse">
        <div className="h-6 bg-gray-300 rounded w-1/3" />
        <div className="h-14 bg-gray-300 rounded w-2/3" />
        <div className="h-6 bg-gray-300 rounded w-1/3" />
        <div className="h-14 bg-gray-300 rounded w-2/3" />
      </div>
    );
  }

  return (
    <div className="h-full w-full px-8 flex flex-col gap-5">
      <div>
        <span className="text-lg font-semibold">
          {t('user.asset.label', { count: 2 })}
        </span>
      </div>
      <div className="flex flex-col gap-5">
        {assets && assets.length > 0
          ?
          assets.map((item, index) =>
            <div key={index} className="bg-[#f4f5f7] rounded-lg flex items-center py-4 px-4 gap-4 shadow">
              <img
                alt={item.id}
                src={getImage(item.files)}
                className="w-24"
              />
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-base font-semibold">
                    {item.name}
                  </span>
                  <span className="text-[#808080] text-xs">
                    ({item?.category?.name[language]})
                  </span>
                </div>
                <div className="border border-[#000] rounded-2xl flex justify-center items-center px-4 py-1 w-fit" onClick={() => navigate('/asset/return/' + item.id)}>
                  <span className="text-dark font-medium">
                    {t('asset.return')}
                  </span>
                </div>
              </div>
            </div>
          )
          :
          <span>
            {t('user.no_assets')}
          </span>
        }
      </div>
      <div>
        <span className="text-lg font-semibold">
          {t('user.request.label', { count: 2 })}
        </span>
      </div>
      <div className="flex flex-col gap-5">
        {requests && requests.length > 0
          ?
          <AnimatePresence>
            {requests.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 1, y: 0 }}
                animate={{
                  opacity: deletingRequests[item.id] ? 0.5 : 1,
                  scale: deletingRequests[item.id] ? 0.98 : 1,
                }}
                exit={{ opacity: 0, height: 0, marginBottom: 0, marginTop: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-[#f4f5f7] rounded-lg flex items-center py-4 px-4 gap-4 shadow relative overflow-hidden"
              >
                <img
                  alt={item.id}
                  src={getImage(item?.asset.files)}
                  className="w-24"
                  onClick={() => navigate('/asset/details/' + item?.asset.id)}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-base font-semibold">
                      {item?.asset.name}
                    </span>
                    <span className="text-[#808080] text-xs">
                      ({item?.asset.category?.name[language]})
                    </span>
                  </div>
                  <div
                    className="border border-[#000] rounded-2xl flex justify-center items-center px-4 py-1 w-fit"
                    onClick={() => !deletingRequests[item.id] && handleCancel(item.id)}
                  >
                    {deletingRequests[item.id] ? (
                      <motion.span
                        className="text-dark font-medium flex items-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-dark" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('request.canceling')}
                      </motion.span>
                    ) : (
                      <span className="text-dark font-medium">
                        {t('request.cancel_notify')}
                      </span>
                    )}
                  </div>
                </div>
                {deletingRequests[item.id] && (
                  <motion.div
                    className="absolute inset-0 bg-white"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.2 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          :
          <span>
            {t('user.no_requests')}
          </span>
        }
      </div>
      {/* <div className="flex mt-auto">
        <Button
          onClick={handleLogout}
          color="danger"
          size="sm"
          text={t('auth.logout')}
        />
      </div> */}
    </div>
  )
}

export default OwnedAssets;